import { ComponentType, CSSProperties, FC, useCallback } from 'react';
import { Box, SxProps, Theme, Button, ButtonProps } from '@mui/material';
import { Stripe, StripeElements, StripeElementsOptionsMode } from '@stripe/stripe-js';
import { Elements, ElementsConsumer, PaymentElement } from '@stripe/react-stripe-js';
import { GetPricesAndFeesDto } from '@sweatpals/shared';
import { AppearenceOptions, checkoutFromSubmitButtonId, defaultStripeElementsOptions } from '../../constants';

export interface StripePaymentElementsProps {
	isE2EMode?: boolean;
	stripeService?: Stripe | null;
	priceAndFees?: GetPricesAndFeesDto | null;
	isPaying?: boolean;
	isPricesAndFeesLoading?: boolean;
	submitText?: string;
	Button?: ComponentType;
	buttonProps?: Partial<ButtonProps>;
	appearance?: AppearenceOptions;
	paymentMethodTypes?: StripeElementsOptionsMode['payment_method_types'];
	setupFutureUsage: StripeElementsOptionsMode['setup_future_usage'];
	options?: Omit<StripeElementsOptionsMode, 'appearance'>;
	handlePay?(elements?: StripeElements | null): Promise<void> | void;
	handleMockPay?(): Promise<void> | void;
}

export const StripePaymentElements: FC<StripePaymentElementsProps> = (props) => {
	const {
		isE2EMode,
		stripeService,
		priceAndFees,
		isPaying,
		isPricesAndFeesLoading,
		Button: CustomButton,
		handlePay,
		handleMockPay,
		submitText,
		buttonProps,
		appearance,
		paymentMethodTypes,
		setupFutureUsage,
		options,
	} = props;

	const createPaymentButton = useCallback(
		(elements?: StripeElements | null) => (
			<Box sx={{ display: 'flex', justifyContent: 'center' }}>
				<Box
					component={CustomButton || Button}
					{...buttonProps}
					sx={buttonProps?.sx}
					id={buttonProps?.id ?? checkoutFromSubmitButtonId}
					disabled={buttonProps?.disabled || isPricesAndFeesLoading || isPaying}
					onClick={() => {
						if (isE2EMode) {
							handleMockPay?.();
						} else {
							handlePay?.(elements);
						}
					}}
				>
					{submitText}
				</Box>
			</Box>
		),
		[CustomButton, buttonProps, isE2EMode, handleMockPay, handlePay, submitText]
	);

	if (isE2EMode) {
		return createPaymentButton();
	}

	return stripeService ? (
		<Elements
			stripe={stripeService}
			options={{
				...defaultStripeElementsOptions({
					amount: priceAndFees?.fullPrice ?? 50,
					paymentMethodTypes,
					setupFutureUsage,
					appearance,
				}),
				...options,
			}}
		>
			<ElementsConsumer>
				{({ elements }) => (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '24px',
							opacity: isPaying ? 0.2 : 1,
						}}
					>
						<PaymentElement
							id="payment-element"
							options={{
								layout: 'tabs',
							}}
						/>
						{createPaymentButton(elements)}
					</Box>
				)}
			</ElementsConsumer>
		</Elements>
	) : <></>;
};
